import { pgTable, text, serial, integer, boolean, timestamp, varchar, json, time, numeric, uniqueIndex, index, check, type AnyPgColumn } from "drizzle-orm/pg-core";
import { relations, sql } from "drizzle-orm";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// ==========================================
// MULTI-TENANT: Organizations
// ==========================================

export const organizations = pgTable("organizations", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  slug: varchar("slug", { length: 100 }).notNull().unique(), // URL-friendly identifier
  description: text("description"), // Company description
  logoUrl: text("logo_url"), // Legacy URL path (deprecated, use logoData)
  logoData: text("logo_data"), // Base64 encoded logo image for persistent storage
  logoMimeType: varchar("logo_mime_type", { length: 50 }), // MIME type of logo (e.g., image/png)
  faviconUrl: text("favicon_url"), // Legacy URL path (deprecated, use faviconData)
  faviconData: text("favicon_data"), // Base64 encoded favicon for persistent storage
  faviconMimeType: varchar("favicon_mime_type", { length: 50 }), // MIME type of favicon
  primaryColor: varchar("primary_color", { length: 7 }).default("#2d5016"), // Hex color
  secondaryColor: varchar("secondary_color", { length: 7 }).default("#8b7355"), // Hex color
  accentColor: varchar("accent_color", { length: 7 }).default("#EA590C"), // Accent/highlight color
  companyEmail: text("company_email"), // Contact email
  companyPhone: varchar("company_phone", { length: 20 }), // Contact phone
  companyAddress: text("company_address"), // Business address
  twilioPhoneNumber: varchar("twilio_phone_number", { length: 20 }).unique(), // SMS sending phone number for this org. UNIQUE: reverse-lookup key for inbound SMS agent routing (getOrganizationByTwilioPhoneNumber). Postgres treats NULLs as distinct, so the many orgs without a number do not collide.
  kioskPin: varchar("kiosk_pin", { length: 255 }), // Hashed PIN for unlocking kiosk mode
  kioskSessionTimeout: integer("kiosk_session_timeout").default(60), // Session timeout in minutes (default 1 hour)
  // Kiosk mode is a paid add-on, off unless FreshOrder switches it on for the vendor.
  // Off hides every kiosk entry point and refuses a new kiosk session; the PIN and
  // timeout above are kept. Written only by saveKioskEnabled (server/orgSettings.ts),
  // which audits the change — deliberately not in insertOrganizationSchema.
  kioskEnabled: boolean("kiosk_enabled").notNull().default(false),
  isActive: boolean("is_active").notNull().default(true),
  isDemo: boolean("is_demo").notNull().default(false), // Demo orgs never sync to QB, SMS, or email
  // S10 per-org opt-out: when false the org never appears in the pre-auth vendor
  // search (GET /api/vendors/search) that the native launch screen uses. Direct
  // /org/:slug links keep working — this only controls discovery by name.
  discoverable: boolean("discoverable").notNull().default(true),
  // What the org's pick sheet prints beyond Qty | Item | [ X ] (PickSheetSettings, below).
  // NULL = today's sheet exactly. Written by PUT /pick-sheet-settings (validated) and by
  // hand-written SQL (the Pitt backfill), so it is validated again when read.
  pickSheetSettings: json("pick_sheet_settings").$type<PickSheetSettings>(),
  // QuickBooks invoice lines carry the product's official name as their Description. Off =
  // the QuickBooks item's own description, or none. Replaced the CNP org-id check.
  qbLineDescription: boolean("qb_line_description").notNull().default(false),
  // With qb_line_description on, the Description also carries the product's subheading:
  // "name - subheading". Legacy — a subheading is for the customer's menu only (Jon,
  // 2026-10-13). On for CN Produce alone, until its sizes move out of subheadings into
  // Pack. Does nothing while qb_line_description is off. SQL only; no route writes it.
  qbLineSubheading: boolean("qb_line_subheading").notNull().default(false),
  // QuickBooks invoices price a line FreshOrder has no price for at the last price this
  // customer was invoiced for the item, before the QuickBooks item's own price.
  // Settings → Admin → QuickBooks Integration. Was one in-memory flag for every org.
  qbHistoricalPricing: boolean("qb_historical_pricing").notNull().default(true),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  archivedAt: timestamp("archived_at"), // Soft delete - when set, org is archived but recoverable
});

export const organizationCredentials = pgTable("organization_credentials", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").notNull().references(() => organizations.id),
  serviceType: varchar("service_type", { length: 50 }).notNull(), // 'twilio', 'quickbooks', 'gmail'
  encryptedCredentials: json("encrypted_credentials").$type<Record<string, unknown>>(), // Encrypted JSON
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (table) => [
  uniqueIndex("org_service_unique").on(table.organizationId, table.serviceType),
]);

// Order Window Configuration - per-organization settings for order periods
// Frequency options: none (disabled), daily, weekly, biweekly, custom
// Custom days uses a bitmask: bit 0 = Sunday, bit 1 = Monday, ..., bit 6 = Saturday
export const orderWindowConfigs = pgTable("order_window_configs", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").notNull().references(() => organizations.id).unique(),
  enabled: boolean("enabled").notNull().default(false),
  frequency: varchar("frequency", { length: 20 }).notNull().default("weekly"), // 'daily', 'weekly', 'biweekly', 'custom'
  timezone: varchar("timezone", { length: 50 }).notNull().default("America/Toronto"),
  anchorStart: timestamp("anchor_start").notNull().defaultNow(), // Reference point for calculating windows
  cutoffHour: integer("cutoff_hour").notNull().default(17), // Hour of day for cutoff (0-23)
  cutoffMinute: integer("cutoff_minute").notNull().default(0), // Minute for cutoff (0-59)
  customDays: integer("custom_days").notNull().default(0), // Bitmask: bit 0=Sun, 1=Mon, 2=Tue, 3=Wed, 4=Thu, 5=Fri, 6=Sat
  deliveryEnabled: boolean("delivery_enabled").notNull().default(true), // Org offers delivery
  pickupEnabled: boolean("pickup_enabled").notNull().default(false), // Org offers pickup
  pickupFollowsDeliverySchedule: boolean("pickup_follows_delivery_schedule").notNull().default(false), // When true, pickup uses delivery window rules
  pickupSchedulingEnabled: boolean("pickup_scheduling_enabled").notNull().default(false),
  pickupAvailableDays: integer("pickup_available_days").notNull().default(62), // Bitmask: bit 0=Sun, 1=Mon..6=Sat. Default 62 = Mon-Fri (bits 1-5)
  // Both blackout-date columns re-emit SET DEFAULT '{}' on every db:push: drizzle-kit 0.31 reads
  // an empty-array default back as '{""}' (drizzle-orm#4231). Harmless. Never silence it with
  // [""] — that default is a one-element array.
  pickupBlackoutDates: text("pickup_blackout_dates").array().notNull().default([]), // Array of YYYY-MM-DD strings
  pickupMaxLeadDays: integer("pickup_max_lead_days").notNull().default(7), // Max days ahead for pickup: 0=same day only, 1=next day, 7=one week, etc.
  pickupCutoffHour: integer("pickup_cutoff_hour").notNull().default(17), // Hour of day for pickup cutoff (0-23)
  pickupCutoffMinute: integer("pickup_cutoff_minute").notNull().default(0), // Minute for pickup cutoff (0-59)
  pickupPrepDays: integer("pickup_prep_days").notNull().default(0), // Min days between ordering and pickup: 0=same day, 1=next day
  deliveryLeadDays: integer("delivery_lead_days").notNull().default(1),
  deliveryMaxLeadDays: integer("delivery_max_lead_days").notNull().default(7),
  deliveryBlackoutDates: text("delivery_blackout_dates").array().notNull().default([]),
  autoChangeNotes: boolean("auto_change_notes").notNull().default(true),
  // "Delivery days: same for everyone / vary by zone" (BRIEF-customer-types-zones-picksheet.md
  // §5 row 10). Off: delivery_zones rows are kept but ignored. On: a zoned account's delivery
  // days are its zone's days ∩ the org's (resolveOrderWindowConfig); an unzoned account's
  // are the org's.
  deliveryDaysByZone: boolean("delivery_days_by_zone").notNull().default(false),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

// Org-level notification defaults
// Accounts inherit these unless they have explicit overrides (null = inherit)
// Reminder times default to '08:00:00', as Postgres stores them. '08:00' is the same time,
// but drizzle-kit compares the text, so every db:push re-emitted SET DEFAULT '08:00'.
export const notificationDefaults = pgTable("notification_defaults", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").notNull().references(() => organizations.id).unique(),

  // SMS defaults
  smsEnabled: boolean("sms_enabled").notNull().default(false),
  smsTemplate: text("sms_template"),
  smsFirstReminderDay: varchar("sms_first_reminder_day", { length: 10 }).notNull().default("monday"),
  smsFirstReminderTime: time("sms_first_reminder_time").notNull().default("08:00:00"),
  smsSecondReminderDay: varchar("sms_second_reminder_day", { length: 10 }).notNull().default("wednesday"),
  smsSecondReminderTime: time("sms_second_reminder_time").notNull().default("08:00:00"),
  smsOrderReceipts: boolean("sms_order_receipts").notNull().default(false),

  // Email defaults
  emailEnabled: boolean("email_enabled").notNull().default(false),
  emailTemplate: text("email_template"),
  emailFirstReminderDay: varchar("email_first_reminder_day", { length: 10 }).notNull().default("monday"),
  emailFirstReminderTime: time("email_first_reminder_time").notNull().default("08:00:00"),
  emailSecondReminderDay: varchar("email_second_reminder_day", { length: 10 }).notNull().default("wednesday"),
  emailSecondReminderTime: time("email_second_reminder_time").notNull().default("08:00:00"),
  emailOrderReceipts: boolean("email_order_receipts").notNull().default(false),

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Org-level market report — an optional, admin-editable notice shown at the top
// of the customer order page (styled like the first "category"). One row per org.
// OFF by default. Pure content — no SMS/email/QB involvement.
export const marketReports = pgTable("market_reports", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").notNull().references(() => organizations.id).unique(),
  enabled: boolean("enabled").notNull().default(false),     // OFF by default
  title: text("title").notNull().default("Market Report"),  // card heading, admin-editable
  body: text("body"),                                        // report text (plain text, newlines preserved)
  contentUpdatedAt: timestamp("content_updated_at").notNull().defaultNow(), // freshness signal — bumped only when body changes
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

// Admin order alert subscriptions
// Each row = one person who is told about order activity in an org — by text, by email,
// or both (Jon, 2026-10-16: email is its own option, not a copy of the text).
export const orderAlertSubscriptions = pgTable("order_alert_subscriptions", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").notNull().references(() => organizations.id),

  // Recipient info — at least one destination (the check below)
  name: text("name").notNull(),                    // Display name (e.g., "Carlos - Warehouse")
  phone: varchar("phone", { length: 20 }),          // SMS destination; null = no texts
  email: text("email"),                             // email destination; null = no emails

  // Which events trigger alerts
  alertOnNewOrder: boolean("alert_on_new_order").notNull().default(true),
  alertOnOrderUpdate: boolean("alert_on_order_update").notNull().default(true),
  alertOnOrderCancel: boolean("alert_on_order_cancel").notNull().default(true),

  // The ACTIVE window — when alerts ARE sent — on the org's clock (shared/orderAlertWindow.ts).
  // null times = any hour (both set or neither); null days = every day. Settings called the
  // times "quiet hours" until 2026-10-16; the data never was, and no row needed inverting.
  activeStartTime: time("active_start_time"),       // e.g., "06:00" — alerts start
  activeEndTime: time("active_end_time"),           // e.g., "20:00" — alerts stop
  activeDays: integer("active_days"),               // bitmask, bit 0 = Sun … bit 6 = Sat

  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (table) => [
  check("order_alert_subscriptions_has_destination", sql`${table.phone} IS NOT NULL OR ${table.email} IS NOT NULL`),
]);

export const accounts = pgTable("accounts", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").notNull().references(() => organizations.id),
  name: text("name").notNull(),
  phone: varchar("phone", { length: 20 }), // Optional - used for SMS and magic link recovery
  customUrl: text("custom_url"),
  isActive: boolean("is_active").notNull().default(true),
  pin: varchar("pin", { length: 255 }), // Hashed 4-6 digit PIN for customer login
  pinSetAt: timestamp("pin_set_at"), // When PIN was last set/changed
  requiresPinSetup: boolean("requires_pin_setup").notNull().default(true), // Flag for accounts needing PIN setup
  failedPinAttempts: integer("failed_pin_attempts").notNull().default(0), // B6b: consecutive failed PIN logins; reset to 0 on success
  pinLockedUntil: timestamp("pin_locked_until"), // B6b: while in the future, PIN login is locked (brute-force backoff)
  customMenu: json("custom_menu").$type<CustomMenuConfig>(),
  reminderEnabled: boolean("reminder_enabled"),             // null = inherit org default
  customReminderMessage: text("custom_reminder_message"),
  firstReminderDay: varchar("first_reminder_day", { length: 10 }),    // null = inherit
  firstReminderTime: time("first_reminder_time"),                      // null = inherit
  secondReminderDay: varchar("second_reminder_day", { length: 10 }),   // null = inherit
  secondReminderTime: time("second_reminder_time"),                    // null = inherit
  smsOrderReceipts: boolean("sms_order_receipts"),           // null = inherit org default
  emailNotificationEnabled: boolean("email_notification_enabled"),     // null = inherit
  emailAddress: text("email_address"),
  customEmailMessage: text("custom_email_message"),
  firstEmailDay: varchar("first_email_day", { length: 10 }),       // null = inherit
  firstEmailTime: time("first_email_time"),                         // null = inherit
  secondEmailDay: varchar("second_email_day", { length: 10 }),      // null = inherit
  secondEmailTime: time("second_email_time"),                       // null = inherit
  emailOrderReceipts: boolean("email_order_receipts"),   // null = inherit org default
  quickbooksCustomerId: varchar("quickbooks_customer_id", { length: 50 }),
  parentAccountId: integer("parent_account_id"),
  isGroupParent: boolean("is_group_parent").notNull().default(false),
  // Per-account fulfillment overrides (null = inherit org defaults)
  fulfillmentDelivery: boolean("fulfillment_delivery"),   // null = inherit, true = enabled, false = disabled
  fulfillmentPickup: boolean("fulfillment_pickup"),       // null = inherit, true = enabled, false = disabled
  // Customer type (BRIEF-customer-types-zones-picksheet.md §2a): the menu exclusions and
  // price list this account orders under. null = the org's default menu and prices.
  // Set ONLY through PUT /accounts/:id/customer-type, which checks the type is this
  // org's — never add it to insertAccountSchema's pick (PATCH /accounts/:id).
  customerTypeId: integer("customer_type_id").references(() => customerTypes.id, { onDelete: "set null" }),
  // Address (BRIEF-customer-types-zones-picksheet.md §2d, Thread C1). A linked account's
  // comes from QuickBooks (ShipAddr, else BillAddr) via POST /quickbooks/sync/refresh-
  // addresses; an admin can type one in (PUT /accounts/:id/address), which marks it
  // 'manual' so a refresh never overwrites it. Never set through PATCH /accounts/:id.
  // Never served unauthenticated: every one of these is on toPublicAccount's denylist.
  addressLine1: text("address_line1"),
  addressLine2: text("address_line2"),
  city: text("city"),
  province: text("province"),
  postalCode: text("postal_code"),
  addressSource: text("address_source"), // 'quickbooks' | 'manual' | null (never set)
  addressRefreshedAt: timestamp("address_refreshed_at"), // last QuickBooks refresh that wrote it
  // The QuickBooks company (realm id) the address came from. Customer ids repeat across
  // companies, so QB-7b (links per company) needs to know which one this was.
  addressRealmId: varchar("address_realm_id", { length: 50 }),
  // Delivery zone — the account's is the source of truth; address matching only proposes.
  // 'auto' = set by the FSA / town match, which re-runs when the address or the zones
  // change; 'manual' = an admin chose it (a zone, or none), and no match overwrites it.
  deliveryZoneId: integer("delivery_zone_id").references(() => deliveryZones.id, { onDelete: "set null" }),
  deliveryZoneSource: text("delivery_zone_source"), // 'auto' | 'manual' | null
  isDemo: boolean("is_demo").notNull().default(false), // Demo/ephemeral accounts — never get real notifications
  isTemplate: boolean("is_template").notNull().default(false), // Template account — catalog source for new ephemeral accounts
  ephemeralCreatedAt: timestamp("ephemeral_created_at"), // Set on demo accounts; null = permanent (template/real)
  // Account requests (BRIEF-account-request-flow). `status` is the lifecycle gate:
  //   'active'  — a real customer account (every pre-existing row)
  //   'pending' — a buyer self-requested it from the sign-in card and no admin has
  //               approved it yet. Pending rows are created with isActive=false AND
  //               must be excluded by an explicit status predicate from every
  //               buyer-facing lookup (phone login, authorized-contact match, order
  //               phone-fallback, agent, reminders) — isActive alone is NOT a login
  //               gate (see /customer/login + /customer/set-pin). Decline = delete.
  status: text("status").notNull().default("active"),
  requesterName: text("requester_name"), // Person who filed the request (the account name is the company)
  requestedAt: timestamp("requested_at"),
  approvedAt: timestamp("approved_at"),
  approvedByUserId: integer("approved_by_user_id"),
  // Clyde's trust ladder (docs/clyde/group-thread-transport.md §9). The streak counts
  // the buyer's YESes to a recap Clyde got right first time; it resets to 0 when an
  // order Clyde placed is edited or cancelled in its editable window, or a recap is
  // answered with a change. Written ONLY by server/services/clydeTrust.ts. The pin
  // (always confirm) keeps the account on recap + YES whatever the streak. Never
  // served to a buyer (toPublicAccount's denylist), never in insertAccountSchema.
  clydeConfirmStreak: integer("clyde_confirm_streak").notNull().default(0),
  clydeAlwaysConfirm: boolean("clyde_always_confirm").notNull().default(false),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => [
  uniqueIndex("accounts_org_custom_url_unique").on(table.organizationId, table.customUrl),
  index("accounts_is_demo_idx").on(table.isDemo),
]);

export const ACCOUNT_STATUSES = ["active", "pending"] as const;
export type AccountStatus = (typeof ACCOUNT_STATUSES)[number];

// Magic links for PIN recovery (sent via email or SMS)
export const magicLinks = pgTable("magic_links", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").notNull().references(() => organizations.id),
  accountId: integer("account_id").notNull().references(() => accounts.id),
  token: varchar("token", { length: 64 }).notNull().unique(), // Secure random token
  expiresAt: timestamp("expires_at").notNull(), // Typically 15 minutes
  usedAt: timestamp("used_at"), // Null until used
  sentVia: varchar("sent_via", { length: 10 }).notNull(), // 'email' or 'sms'
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

// Email verification tokens for customers adding email to their account
export const emailVerifications = pgTable("email_verifications", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").notNull().references(() => organizations.id),
  accountId: integer("account_id").notNull().references(() => accounts.id),
  email: text("email").notNull(), // The email being verified
  token: varchar("token", { length: 64 }).notNull().unique(),
  expiresAt: timestamp("expires_at").notNull(), // 24 hours
  usedAt: timestamp("used_at"), // Null until verified
  enableFeature: varchar("enable_feature", { length: 50 }), // Optional: 'emailNotificationEnabled' or 'emailOrderReceipts'
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

// B5: durable reminder-dedup ledger. Replaces the in-memory sentRemindersCache
// so a restart mid-window can't re-send. One row per (org, account, type, date,
// time); the unique index makes the insert-before-send an atomic claim
// (ON CONFLICT DO NOTHING). date/time are the same YYYY-MM-DD / HH:MM strings
// the scheduler already keys on.
export const sentReminders = pgTable("sent_reminders", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").notNull().references(() => organizations.id),
  accountId: integer("account_id").notNull().references(() => accounts.id),
  reminderType: varchar("reminder_type", { length: 16 }).notNull(), // 'first' | 'second'
  date: varchar("date", { length: 10 }).notNull(), // YYYY-MM-DD (send day, org-local)
  time: varchar("time", { length: 5 }).notNull(),  // HH:MM (send minute)
  sentAt: timestamp("sent_at").notNull().defaultNow(),
}, (table) => [
  uniqueIndex("sent_reminders_unique").on(table.organizationId, table.accountId, table.reminderType, table.date, table.time),
]);

// Customer portal sessions (for authenticated order page access)
export const customerSessions = pgTable("customer_sessions", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").notNull().references(() => organizations.id),
  accountId: integer("account_id").notNull().references(() => accounts.id),
  sessionToken: varchar("session_token", { length: 64 }).notNull().unique(),
  expiresAt: timestamp("expires_at").notNull(), // Session expiry (e.g., 7 days)
  createdAt: timestamp("created_at").notNull().defaultNow(),
  lastAccessedAt: timestamp("last_accessed_at").notNull().defaultNow(),
});

// Kiosk sessions (for admin-unlocked kiosk mode access)
export const kioskSessions = pgTable("kiosk_sessions", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").notNull().references(() => organizations.id),
  sessionToken: varchar("session_token", { length: 64 }).notNull().unique(),
  expiresAt: timestamp("expires_at").notNull(), // Session expiry based on org timeout
  createdAt: timestamp("created_at").notNull().defaultNow(),
  lastActivityAt: timestamp("last_activity_at").notNull().defaultNow(),
});

// Admin portal sessions (for authenticated admin dashboard access)
export const adminSessions = pgTable("admin_sessions", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").notNull().references(() => organizations.id),
  userId: integer("user_id").notNull().references(() => users.id),
  sessionToken: text("session_token").notNull().unique(), // stored as SHA-256 hash
  role: text("role").notNull(), // 'admin' or 'staff'
  // Minted by super-admin impersonation (userId is then the org's first admin, borrowed —
  // the super admin has no user row). Requests on it audit as actor 'super_admin'
  // (server/auditActor.ts). Only the impersonate route sets it.
  impersonatedBySuperAdmin: boolean("impersonated_by_super_admin").notNull().default(false),
  expiresAt: timestamp("expires_at").notNull(),
  lastAccessedAt: timestamp("last_accessed_at").notNull().defaultNow(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

// Super admin portal sessions (system-level, not org-scoped, not user-backed today).
// userId is nullable: super admin login validates against env password and is not tied
// to a users row. The column is exposed for future audit use but not validated.
export const superAdminSessions = pgTable("super_admin_sessions", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").references(() => users.id),
  sessionToken: text("session_token").notNull().unique(), // stored as SHA-256 hash
  expiresAt: timestamp("expires_at").notNull(),
  lastAccessedAt: timestamp("last_accessed_at").notNull().defaultNow(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const categories = pgTable("categories", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").notNull().references(() => organizations.id),
  name: text("name").notNull(),
  icon: text("icon").notNull(),
  isActive: boolean("is_active").notNull().default(true),
  isVisibleToCustomers: boolean("is_visible_to_customers").notNull().default(true),
  isSystemCategory: boolean("is_system_category").notNull().default(false),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => [
  uniqueIndex("categories_org_name_unique").on(table.organizationId, table.name),
]);

// Product aliases — colloquial terms buyers actually use ("romas" → "Roma Tomatoes 25lb").
// Consumed by BOTH the SMS agent (deterministic term→product resolution) and the in-app
// product searches (alias-aware filtering). Entries may be labeled objects or plain
// strings (treated as manual+active). Only status 'active' entries ever resolve or match;
// 'suggested' is the agent-learned holding pen awaiting admin approval (Phase 2 of the
// alias work — learned aliases are NEVER auto-applied).
export type ProductAliasSource = "manual" | "learned";
export type ProductAliasStatus = "active" | "suggested";
export interface ProductAliasEntry {
  alias: string;
  source: ProductAliasSource;
  status: ProductAliasStatus;
  at?: string; // ISO timestamp when added/learned
  seenCount?: number; // how often buyers used it (learned entries)
}
export type ProductAlias = string | ProductAliasEntry;

export const products = pgTable("products", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").notNull().references(() => organizations.id),
  name: text("name").notNull(),
  // The two parts the product form takes the name in (shared/productNames.ts): the item
  // ("Oranges") heads the customer's menu card, the pack or size ("9x3lb bag") reads in
  // small print beside it, and `name` is the two joined — what pick sheets, invoices and
  // order lines print. Only the customer menu and the product form read these; `name`
  // stays the product's one official name. item_name NULL = the whole name is the item
  // and there is no pack (a product written by name alone: an import, a QuickBooks pull).
  itemName: text("item_name"),
  pack: text("pack"),
  categoryId: integer("category_id").notNull().references(() => categories.id).default(1),
  isActive: boolean("is_active").notNull().default(true),
  imagePath: text("image_path"),
  imageData: text("image_data"), // Base64 encoded image data for persistence
  imageMimeType: text("image_mime_type"), // MIME type of the stored image
  subheading: text("subheading"),
  // Sell unit: what one ordered quantity means — "case", "each", "lb". Free text, stored
  // lowercased (shared/productUnit.ts). NULL = not stated; nothing prints it.
  unit: varchar("unit", { length: 30 }),
  aliases: json("aliases").$type<ProductAlias[]>(), // colloquial buyer terms (see ProductAliasEntry)
  sortOrder: integer("sort_order").notNull().default(0),
  quickbooksItemId: varchar("quickbooks_item_id", { length: 50 }),
  price: text("price"), // Current unit price (nullable - blank means no price set)
  taxPercent: text("tax_percent"), // Tax percentage for this item (e.g., "13" for 13%), null means no tax
  // Linked sizes (shared/productSizes.ts): this product is another size of the product
  // named here — its half case, its single, its per-pound entry. It stays a full product
  // (own name, price, unit, QuickBooks item, order lines); the link only tells the
  // customer menu to draw the group as one card. One level deep: the product named here
  // is never itself a size. NULL = an ordinary product, drawn as it always was. Set only
  // through server/productSizeRoutes.ts, which checks the org of both ends.
  sizeOfProductId: integer("size_of_product_id").references((): AnyPgColumn => products.id, { onDelete: "set null" }),
  // How much of the linked product one of this size is: 0.5 for a half case, 1/56 for
  // one orange from a case of 56. Stored for a later procurement roll-up; nothing reads it yet.
  caseFraction: numeric("case_fraction"),
  // Where this unit sits among its item's units (0 = first), once the admin has dragged
  // them into an order on the item form. NULL = never ordered: the unit takes the place
  // the card always gave it (lead first, then the largest share of a case), and a unit
  // added after an order was set goes to the end. Written only by PUT
  // /products/:id/unit-order (server/productSizeRoutes.ts).
  unitOrder: integer("unit_order"),
}, (table) => [
  uniqueIndex("products_org_name_unique").on(table.organizationId, table.name),
  index("products_size_of_idx").on(table.organizationId, table.sizeOfProductId),
  check("products_size_of_not_self", sql`${table.sizeOfProductId} IS NULL OR ${table.sizeOfProductId} <> ${table.id}`),
]);

// ==========================================
// Customer types (BRIEF-customer-types-zones-picksheet.md §2a — Thread B)
// ==========================================
// A customer type is a live reference an account points at: which sell units and
// products it never sees, and the prices it pays. Every read and write resolves
// through server/services/menuPricing.ts. An org with no rows here gets today's
// menu and prices unchanged.

export const customerTypes = pgTable("customer_types", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").notNull().references(() => organizations.id),
  name: text("name").notNull(),
  sortOrder: integer("sort_order").notNull().default(0),
  // Sell units (products.unit, normalised by shared/productUnit.ts) this type never sees.
  hiddenUnits: text("hidden_units").array(),
  // Used only when price_mode = 'percent': products.price × (1 + pct/100).
  priceAdjustPercent: numeric("price_adjust_percent"),
  // Which price this type's customers pay — one or the other or neither. The rows of the
  // mode not in use stay stored, unused, so switching back restores them exactly:
  //   base    — products.price
  //   own     — the type's customer_type_prices row where one exists, else products.price
  //   percent — products.price moved by price_adjust_percent
  priceMode: text("price_mode").notNull().default("base"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => [
  uniqueIndex("customer_types_org_name_unique").on(table.organizationId, table.name),
  check("customer_types_price_mode_check", sql`${table.priceMode} in ('base', 'own', 'percent')`),
]);

export const CUSTOMER_TYPE_PRICE_MODES = ["base", "own", "percent"] as const;
export type CustomerTypePriceMode = (typeof CUSTOMER_TYPE_PRICE_MODES)[number];

// Exclusion rules: one row hides one product, or every product in one category.
export const customerTypeMenuRules = pgTable("customer_type_menu_rules", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").notNull().references(() => organizations.id),
  customerTypeId: integer("customer_type_id").notNull().references(() => customerTypes.id, { onDelete: "cascade" }),
  productId: integer("product_id").references(() => products.id, { onDelete: "cascade" }),
  categoryId: integer("category_id").references(() => categories.id, { onDelete: "cascade" }),
  hidden: boolean("hidden").notNull().default(true),
}, (table) => [
  check("customer_type_menu_rules_one_target", sql`(${table.productId} IS NULL) <> (${table.categoryId} IS NULL)`),
  uniqueIndex("customer_type_menu_rules_type_product_unique").on(table.customerTypeId, table.productId),
  uniqueIndex("customer_type_menu_rules_type_category_unique").on(table.customerTypeId, table.categoryId),
  index("customer_type_menu_rules_org_idx").on(table.organizationId),
]);

// The type's price list: an explicit price per product, beating the percent default.
export const customerTypePrices = pgTable("customer_type_prices", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").notNull().references(() => organizations.id),
  customerTypeId: integer("customer_type_id").notNull().references(() => customerTypes.id, { onDelete: "cascade" }),
  productId: integer("product_id").notNull().references(() => products.id, { onDelete: "cascade" }),
  price: text("price").notNull(),
}, (table) => [
  uniqueIndex("customer_type_prices_type_product_unique").on(table.customerTypeId, table.productId),
  index("customer_type_prices_org_idx").on(table.organizationId),
]);

// ==========================================
// DELIVERY ZONES (BRIEF-customer-types-zones-picksheet.md §2d, Thread C1)
// ==========================================
// A zone groups accounts that share a truck: its delivery days narrow the org's (never
// add one — §5 row 11), and only while order_window_configs.delivery_days_by_zone is on.
// Accounts are matched to a zone by postal FSA ("N0G"), else by town.

export const deliveryZones = pgTable("delivery_zones", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").notNull().references(() => organizations.id),
  name: text("name").notNull(),
  deliveryDays: integer("delivery_days").notNull().default(0), // bitmask, as customDays: bit 0=Sun … bit 6=Sat
  // No array defaults: drizzle-kit re-emits SET DEFAULT '{}' on every push (drizzle-orm#4231).
  // Every insert names both.
  postalPrefixes: text("postal_prefixes").array().notNull(), // FSAs, upper case, e.g. "N0G"
  cities: text("cities").array().notNull(), // towns as typed; matched case-insensitively
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => [
  // Leads with organization_id, so it is also the org index.
  uniqueIndex("delivery_zones_org_name_unique").on(table.organizationId, table.name),
]);

export const orders = pgTable("orders", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").notNull().references(() => organizations.id),
  accountId: integer("account_id").notNull().references(() => accounts.id),
  status: text("status").notNull().default("pending"),
  totalItems: integer("total_items").notNull().default(0),
  notes: text("notes"),
  isKioskOrder: boolean("is_kiosk_order").notNull().default(false),
  deliveryLocation: text("delivery_location"),
  pickupDate: text("pickup_date"), // Legacy - superseded by fulfillmentDate, kept for backward compatibility
  fulfillmentDate: text("fulfillment_date"), // Date order needs to be fulfilled (YYYY-MM-DD format)
  fulfillmentType: text("fulfillment_type"), // "delivery" or "pickup"
  submissionId: text("submission_id"), // Client-generated UUID for idempotency — prevents duplicate orders on network retry
  // Order origin signal — set at create time by each route. "admin_manual" is the
  // conservative default so any code path that forgets to set it gets the auditable
  // answer. Replaces the submission_id-based heuristic (which can't distinguish
  // pre-2026-03-23 customer-app orders from admin-entered legacy orders).
  // Backfill: rows with submissionId IS NOT NULL were customer_app (or kiosk if
  // is_kiosk_order = true); pre-launch rows stay 'admin_manual' as a conservative
  // default — see BRIEF-order-source-tracking.md.
  source: text("source").notNull().default("admin_manual"), // "customer_app" | "kiosk" | "admin_manual" | "sms_agent"
  // Clyde (the text-to-order agent): the thread this order came out of, and the
  // buyer's (or the rep's forwarded) message whose text became the order. Stamped by
  // createAgentOrderFromPending on create AND on a per-date consolidation (the latest
  // message wins); nothing else writes them. Nullable — every other source leaves
  // them NULL — and read org-scoped only (an id from another org never resolves).
  // The orders page's text bubble and the console's "Order #N" link ride on these.
  agentConversationId: integer("agent_conversation_id").references(() => agentConversations.id),
  agentMessageId: integer("agent_message_id").references(() => agentMessages.id),
  isDemo: boolean("is_demo").notNull().default(false), // Demo orders — excluded from QB, SMS, email
  quickbooksInvoiceId: text("quickbooks_invoice_id"), // QB invoice "Id" string — null = not sent
  quickbooksSyncedAt: timestamp("quickbooks_synced_at"), // Sent marker — null = not sent
  quickbooksSyncInProgress: timestamp("quickbooks_sync_in_progress"), // B4: atomic dup-send claim — set when a send is claimed, cleared on success/failure; null = unclaimed. Stale claims (older than the TTL) are swept.
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (table) => [
  index("orders_is_demo_idx").on(table.isDemo),
  // T1-2: the hottest read paths filter by org and either sort by createdAt
  // (dashboards/lists) or filter by account (account order history / current
  // period). Without these, every such read is a shared-table Seq Scan that
  // grows linearly with the whole fleet's orders.
  index("orders_org_created_idx").on(table.organizationId, table.createdAt),
  index("orders_org_account_idx").on(table.organizationId, table.accountId),
]);

export const orderItems = pgTable("order_items", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").notNull().references(() => organizations.id),
  orderId: integer("order_id").notNull().references(() => orders.id),
  productId: integer("product_id").notNull().references(() => products.id),
  quantity: integer("quantity").notNull().default(0),
  originalQuantity: integer("original_quantity").notNull().default(0),
  procuredQuantity: integer("procured_quantity").notNull().default(0),
  procurementModified: boolean("procurement_modified").notNull().default(false),
  isFullyProcured: boolean("is_fully_procured").notNull().default(false),
  isPending: boolean("is_pending").notNull().default(false),
  unitPrice: text("unit_price"), // Price at time of order (captures from product price) — or the price staff typed for this order (price_typed)
  // Staff typed this line's price for this order (Jon, 2026-10-16; shared/linePrice.ts). A
  // typed price survives a customer resubmit and is what the QuickBooks send uses; a
  // captured one (false) is only a record — the send prices the line afresh.
  priceTyped: boolean("price_typed").notNull().default(false),
  taxPercent: text("tax_percent"), // Tax percentage at time of order (captures from product taxPercent)
}, (table) => [
  // T1-2: items are read by order_id (order-detail hydration + every list join).
  // The prod EXPLAIN showed a Seq Scan reading 42,859 rows to return 6 — this
  // index turns that into an index seek. org column included so org-scoped
  // point reads (WHERE org=? AND order_id=?) stay index-served too.
  index("order_items_order_id_idx").on(table.orderId, table.organizationId),
]);

// Aggregate procurement: how many units of a product were procured for one fulfillment
// window (delivery day). SOURCE OF TRUTH for procurement — decoupled from
// order_items.procuredQuantity so editing an order's quantity never alters procurement
// (that per-line coupling caused the "reduce a booked order → procured drops" bug).
// Ordered totals stay DERIVED from order_items; only the procured aggregate lives here.
export const productProcurement = pgTable("product_procurement", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").notNull().references(() => organizations.id),
  productId: integer("product_id").notNull().references(() => products.id),
  // YYYY-MM-DD fulfillment date, or the sentinel 'unscheduled' for dateless orders.
  fulfillmentDate: text("fulfillment_date").notNull(),
  procuredQuantity: integer("procured_quantity").notNull().default(0),
  isFullyProcured: boolean("is_fully_procured").notNull().default(false),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (table) => [
  uniqueIndex("product_procurement_org_product_date_unique").on(table.organizationId, table.productId, table.fulfillmentDate),
]);

export const users = pgTable("users", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id), // Null for super admins
  username: text("username").notNull(),
  email: text("email"), // Global login identifier - must be unique when set
  password: text("password").notNull(),
  role: text("role").notNull().default("staff"), // "super_admin", "admin", or "staff"
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  lastLoginAt: timestamp("last_login_at"),
  passwordResetToken: text("password_reset_token"),
  passwordResetExpiry: timestamp("password_reset_expiry"),
}, (table) => [
  uniqueIndex("users_org_username_unique").on(table.organizationId, table.username),
  uniqueIndex("users_email_unique").on(table.email),
]);

export const integrationSettings = pgTable("integration_settings", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").notNull().references(() => organizations.id),
  service: text("service").notNull(),
  isAuthenticated: boolean("is_authenticated").notNull().default(false),
  accessToken: text("access_token"),
  refreshToken: text("refresh_token"),
  companyId: text("company_id"),
  expiresAt: timestamp("expires_at"),
  authData: json("auth_data"),
  lastSync: timestamp("last_sync"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (table) => [
  uniqueIndex("integration_settings_org_service_unique").on(table.organizationId, table.service),
]);

export const auditLogs = pgTable("audit_logs", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").notNull().references(() => organizations.id),
  entityType: text("entity_type").notNull(),
  entityId: integer("entity_id").notNull(),
  action: text("action").notNull(),
  oldValue: text("old_value"),
  newValue: text("new_value"),
  metadata: json("metadata").$type<{
    accountId?: number;
    accountName?: string;
    orderId?: number;
    productId?: number;
    productName?: string;
    userId?: number;
    username?: string;
    // 'super_admin' = written during a super-admin impersonation session; userId is
    // then the borrowed org admin's, not who acted (server/auditActor.ts).
    actor?: "super_admin";
    // Type only (json column, no DDL): an order edit made after the QuickBooks send
    // (server/orderEditAudit.ts). The invoice in QuickBooks does not follow it.
    afterQuickBooksSend?: boolean;
    quickbooksInvoiceId?: string;
    [key: string]: unknown;
  }>(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => [
  // T1-2: audit_logs is read as `WHERE org=? ORDER BY created_at DESC LIMIT n`.
  // The prod EXPLAIN was already 63 ms (Seq Scan + top-N heapsort) at only 4k
  // rows on this never-pruned, fastest-growing table. This composite turns it
  // into an index range scan + limit.
  index("audit_logs_org_created_idx").on(table.organizationId, table.createdAt),
]);

// Define relations
export const orderWindowConfigsRelations = relations(orderWindowConfigs, ({ one }) => ({
  organization: one(organizations, {
    fields: [orderWindowConfigs.organizationId],
    references: [organizations.id],
  }),
}));

export const organizationsRelations = relations(organizations, ({ many }) => ({
  credentials: many(organizationCredentials),
  accounts: many(accounts),
  categories: many(categories),
  products: many(products),
  orders: many(orders),
  users: many(users),
  integrationSettings: many(integrationSettings),
  auditLogs: many(auditLogs),
  orderAlertSubscriptions: many(orderAlertSubscriptions),
}));

export const orderAlertSubscriptionsRelations = relations(orderAlertSubscriptions, ({ one }) => ({
  organization: one(organizations, {
    fields: [orderAlertSubscriptions.organizationId],
    references: [organizations.id],
  }),
}));

export const organizationCredentialsRelations = relations(organizationCredentials, ({ one }) => ({
  organization: one(organizations, {
    fields: [organizationCredentials.organizationId],
    references: [organizations.id],
  }),
}));

export const accountsRelations = relations(accounts, ({ one, many }) => ({
  organization: one(organizations, {
    fields: [accounts.organizationId],
    references: [organizations.id],
  }),
  orders: many(orders),
}));

export const categoriesRelations = relations(categories, ({ one, many }) => ({
  organization: one(organizations, {
    fields: [categories.organizationId],
    references: [organizations.id],
  }),
  products: many(products),
}));

export const productsRelations = relations(products, ({ one, many }) => ({
  organization: one(organizations, {
    fields: [products.organizationId],
    references: [organizations.id],
  }),
  category: one(categories, {
    fields: [products.categoryId],
    references: [categories.id],
  }),
  orderItems: many(orderItems),
}));

export const ordersRelations = relations(orders, ({ one, many }) => ({
  organization: one(organizations, {
    fields: [orders.organizationId],
    references: [organizations.id],
  }),
  account: one(accounts, {
    fields: [orders.accountId],
    references: [accounts.id],
  }),
  items: many(orderItems),
}));

export const orderItemsRelations = relations(orderItems, ({ one }) => ({
  organization: one(organizations, {
    fields: [orderItems.organizationId],
    references: [organizations.id],
  }),
  order: one(orders, {
    fields: [orderItems.orderId],
    references: [orders.id],
  }),
  product: one(products, {
    fields: [orderItems.productId],
    references: [products.id],
  }),
}));

export const usersRelations = relations(users, ({ one }) => ({
  organization: one(organizations, {
    fields: [users.organizationId],
    references: [organizations.id],
  }),
}));

export const integrationSettingsRelations = relations(integrationSettings, ({ one }) => ({
  organization: one(organizations, {
    fields: [integrationSettings.organizationId],
    references: [organizations.id],
  }),
}));

export const auditLogsRelations = relations(auditLogs, ({ one }) => ({
  organization: one(organizations, {
    fields: [auditLogs.organizationId],
    references: [organizations.id],
  }),
}));

// ==========================================
// Insert Schemas
// ==========================================

export const insertOrganizationSchema = createInsertSchema(organizations).pick({
  name: true,
  slug: true,
  description: true,
  logoUrl: true,
  faviconUrl: true,
  primaryColor: true,
  secondaryColor: true,
  accentColor: true,
  companyEmail: true,
  companyPhone: true,
  companyAddress: true,
  twilioPhoneNumber: true,
  kioskPin: true,
  kioskSessionTimeout: true,
  isActive: true,
  discoverable: true,
});

export const insertOrganizationCredentialsSchema = createInsertSchema(organizationCredentials).pick({
  organizationId: true,
  serviceType: true,
  encryptedCredentials: true,
  isActive: true,
});

export const insertOrderWindowConfigSchema = createInsertSchema(orderWindowConfigs).pick({
  organizationId: true,
  enabled: true,
  frequency: true,
  timezone: true,
  anchorStart: true,
  cutoffHour: true,
  cutoffMinute: true,
  customDays: true,
  deliveryEnabled: true,
  pickupEnabled: true,
  pickupFollowsDeliverySchedule: true,
  pickupSchedulingEnabled: true,
  pickupAvailableDays: true,
  pickupBlackoutDates: true,
  pickupMaxLeadDays: true,
  pickupCutoffHour: true,
  pickupCutoffMinute: true,
  pickupPrepDays: true,
  deliveryLeadDays: true,
  deliveryMaxLeadDays: true,
  deliveryBlackoutDates: true,
  deliveryDaysByZone: true,
});

export const insertAccountSchema = createInsertSchema(accounts).pick({
  organizationId: true,
  name: true,
  phone: true,
  customUrl: true,
  isActive: true,
  pin: true,
  pinSetAt: true,
  requiresPinSetup: true,
  customMenu: true,
  reminderEnabled: true,
  customReminderMessage: true,
  firstReminderDay: true,
  firstReminderTime: true,
  secondReminderDay: true,
  secondReminderTime: true,
  smsOrderReceipts: true,
  emailNotificationEnabled: true,
  emailAddress: true,
  customEmailMessage: true,
  firstEmailDay: true,
  firstEmailTime: true,
  secondEmailDay: true,
  secondEmailTime: true,
  emailOrderReceipts: true,
  quickbooksCustomerId: true,
  parentAccountId: true,
  isGroupParent: true,
  fulfillmentDelivery: true,
  fulfillmentPickup: true,
  isDemo: true,
  isTemplate: true,
  ephemeralCreatedAt: true,
  status: true,
  requesterName: true,
  requestedAt: true,
  approvedAt: true,
  approvedByUserId: true,
});

export const insertNotificationDefaultsSchema = createInsertSchema(notificationDefaults).pick({
  organizationId: true,
  smsEnabled: true,
  smsTemplate: true,
  smsFirstReminderDay: true,
  smsFirstReminderTime: true,
  smsSecondReminderDay: true,
  smsSecondReminderTime: true,
  smsOrderReceipts: true,
  emailEnabled: true,
  emailTemplate: true,
  emailFirstReminderDay: true,
  emailFirstReminderTime: true,
  emailSecondReminderDay: true,
  emailSecondReminderTime: true,
  emailOrderReceipts: true,
});

export const insertMarketReportSchema = createInsertSchema(marketReports).pick({
  organizationId: true,
  enabled: true,
  title: true,
  body: true,
});

export const insertOrderAlertSubscriptionSchema = createInsertSchema(orderAlertSubscriptions).pick({
  organizationId: true,
  name: true,
  phone: true,
  email: true,
  alertOnNewOrder: true,
  alertOnOrderUpdate: true,
  alertOnOrderCancel: true,
  activeStartTime: true,
  activeEndTime: true,
  activeDays: true,
  isActive: true,
});

// What a tenant admin may send for a subscriber (POST and PATCH /order-alerts). Strict: any
// other key — organizationId above all — is a 400 and nothing is written (the ACC-1 lesson;
// PATCH passed req.body straight to the update until 2026-10-16). Cross-field rules (a
// destination, both times or neither) are checked on the merged row in the route.
const alertTime = z.string().regex(/^\d{2}:\d{2}(:\d{2})?$/, "Times are HH:MM");
export const orderAlertSubscriptionBodySchema = z.object({
  name: z.string().trim().min(1, "Enter a name").max(80),
  // "" and null both mean "none" (the route stores null); the row check wants one of the two.
  phone: z.string().trim().max(20).nullable().optional(),
  email: z.union([z.literal(""), z.string().trim().email("Enter a valid email address").max(254)]).nullable().optional(),
  alertOnNewOrder: z.boolean().optional(),
  alertOnOrderUpdate: z.boolean().optional(),
  alertOnOrderCancel: z.boolean().optional(),
  activeStartTime: alertTime.nullable().optional(),
  activeEndTime: alertTime.nullable().optional(),
  activeDays: z.number().int().min(1, "Pick at least one day").max(127).nullable().optional(),
  isActive: z.boolean().optional(),
}).strict();
export const orderAlertSubscriptionPatchSchema = orderAlertSubscriptionBodySchema.partial().strict();

export const insertMagicLinkSchema = createInsertSchema(magicLinks).pick({
  organizationId: true,
  accountId: true,
  token: true,
  expiresAt: true,
  sentVia: true,
});

export const insertEmailVerificationSchema = createInsertSchema(emailVerifications).pick({
  organizationId: true,
  accountId: true,
  email: true,
  token: true,
  expiresAt: true,
  enableFeature: true,
});

export const insertCustomerSessionSchema = createInsertSchema(customerSessions).pick({
  organizationId: true,
  accountId: true,
  sessionToken: true,
  expiresAt: true,
});

export const insertKioskSessionSchema = createInsertSchema(kioskSessions).pick({
  organizationId: true,
  sessionToken: true,
  expiresAt: true,
});

export const insertAdminSessionSchema = createInsertSchema(adminSessions).pick({
  organizationId: true,
  userId: true,
  sessionToken: true,
  role: true,
  expiresAt: true,
});

export const insertSuperAdminSessionSchema = createInsertSchema(superAdminSessions).pick({
  userId: true,
  sessionToken: true,
  expiresAt: true,
});

export const insertCategorySchema = createInsertSchema(categories).pick({
  organizationId: true,
  name: true,
  icon: true,
  isActive: true,
  isVisibleToCustomers: true,
  isSystemCategory: true,
  sortOrder: true,
});

export const insertProductSchema = createInsertSchema(products).pick({
  organizationId: true,
  name: true,
  itemName: true,
  pack: true,
  categoryId: true,
  isActive: true,
  imagePath: true,
  subheading: true,
  unit: true,
  aliases: true,
  sortOrder: true,
  quickbooksItemId: true,
  price: true,
  taxPercent: true,
});

// Product categories mapping based on the PDF
export const PRODUCT_CATEGORIES = {
  1: { id: 1, name: "Potatoes", icon: "🥔" },
  2: { id: 2, name: "Onions", icon: "🧅" },
  3: { id: 3, name: "Root Vegetables", icon: "🥕" },
  4: { id: 4, name: "Squash", icon: "🎃" },
  5: { id: 5, name: "Mushrooms", icon: "🍄" },
  6: { id: 6, name: "Lettuce & Greens", icon: "🥬" },
  7: { id: 7, name: "Salad Kits", icon: "🥗" },
  8: { id: 8, name: "Berries", icon: "🫐" },
  9: { id: 9, name: "Stone Fruit", icon: "🍑" },
  10: { id: 10, name: "Vegetables", icon: "🫛" },
  11: { id: 11, name: "Citrus", icon: "🍋" },
  12: { id: 12, name: "Apples", icon: "🍎" },
  13: { id: 13, name: "Peppers", icon: "🌶️" },
  14: { id: 14, name: "Tomatoes", icon: "🍅" },
  15: { id: 15, name: "Fruit", icon: "🍇" },
  17: { id: 17, name: "Cut Flowers", icon: "🌸" },
  18: { id: 18, name: "Pickling Cucumbers", icon: "🥒" }
} as const;

// Custom menu configuration types
export type CustomMenuConfig = {
  categories: Record<string, boolean>; // categoryId -> enabled
  products: Record<string, boolean>; // productId -> enabled
};

// Pick-sheet options (organizations.pick_sheet_settings). Every option is off unless
// set; with none on, the sheet is exactly the default Qty | Item | [ X ] with the order's
// created date in the header. BRIEF-customer-types-zones-picksheet.md §2c.
export const PICK_SHEET_EXTRA_COLUMNS_MAX = 3;
export const PICK_SHEET_COLUMN_LABEL_MAX = 16;
export const pickSheetExtraColumnSchema = z.object({
  label: z.string().trim().min(1, "Name the column").max(PICK_SHEET_COLUMN_LABEL_MAX, `Keep a column name to ${PICK_SHEET_COLUMN_LABEL_MAX} characters`),
  width: z.number().int().min(30).max(80).optional(), // points; the builder's default is 50
}).strict();
export const pickSheetSettingsSchema = z.object({
  showUnitColumn: z.boolean().optional(), // a Unit column after Qty (products.unit)
  // Retired 2026-10-13: a subheading is for the customer's menu only and prints on no
  // pick sheet. The key is still accepted so settings stored with it keep reading (the
  // schema is strict); nothing prints it and a save drops it.
  showSubheading: z.boolean().optional(),
  showFulfillmentDate: z.boolean().optional(), // header: fulfillment day + delivery/pickup instead of the created date
  showCity: z.boolean().optional(), // header: the account's town (accounts.city), when it has one
  extraColumns: z.array(pickSheetExtraColumnSchema)
    .max(PICK_SHEET_EXTRA_COLUMNS_MAX, `At most ${PICK_SHEET_EXTRA_COLUMNS_MAX} blank columns`)
    .optional(), // blank columns to write in by hand, e.g. a lot "Code"
}).strict();
export type PickSheetSettings = z.infer<typeof pickSheetSettingsSchema>;

export const insertOrderSchema = createInsertSchema(orders).pick({
  organizationId: true,
  accountId: true,
  status: true,
  totalItems: true,
  notes: true,
  isKioskOrder: true,
  deliveryLocation: true,
  pickupDate: true,
  fulfillmentDate: true,
  fulfillmentType: true,
  source: true,
});

export const insertOrderItemSchema = createInsertSchema(orderItems).pick({
  organizationId: true,
  orderId: true,
  productId: true,
  quantity: true,
  originalQuantity: true,
  procuredQuantity: true,
  procurementModified: true,
  isFullyProcured: true,
  isPending: true,
  unitPrice: true,
  priceTyped: true,
  taxPercent: true,
});

export const insertProductProcurementSchema = createInsertSchema(productProcurement).pick({
  organizationId: true,
  productId: true,
  fulfillmentDate: true,
  procuredQuantity: true,
  isFullyProcured: true,
});
export type ProductProcurement = typeof productProcurement.$inferSelect;
export type InsertProductProcurement = typeof productProcurement.$inferInsert;

export const insertUserSchema = createInsertSchema(users).pick({
  organizationId: true,
  username: true,
  email: true,
  password: true,
  role: true,
  isActive: true,
});

export const insertIntegrationSettingsSchema = createInsertSchema(integrationSettings).pick({
  organizationId: true,
  service: true,
  isAuthenticated: true,
  accessToken: true,
  refreshToken: true,
  companyId: true,
  expiresAt: true,
  authData: true,
  lastSync: true,
});

export const insertAuditLogSchema = createInsertSchema(auditLogs).pick({
  organizationId: true,
  entityType: true,
  entityId: true,
  action: true,
  oldValue: true,
  newValue: true,
  metadata: true,
});

// Customer order input schema
export const customerOrderSchema = z.object({
  phone: z.string().refine(v => v.length === 0 || v.length >= 7, "Phone number must be at least 7 digits"),
  quantities: z.record(z.string(), z.number().min(0, "Quantity must be 0 or greater").optional().default(0)).default({}),
  // OW-1: the order's lines keyed by product id. When present the server uses these and
  // ignores `quantities` (which is keyed by product NAME, so a rename between menu load and
  // submit used to drop the line silently). Optional so ordering pages loaded before this
  // shipped keep working on `quantities` alone.
  items: z.array(z.object({
    productId: z.number().int().positive(),
    quantity: z.number().int().min(0).max(100000),
  })).max(2000).optional(),
  notes: z.string().optional(),
  isKioskOrder: z.boolean().optional(),
  pickupDate: z.string().optional(), // Legacy - kept for backward compatibility
  fulfillmentDate: z.string().optional(), // YYYY-MM-DD format
  fulfillmentType: z.enum(["delivery", "pickup"]).optional(),
  sessionToken: z.string().optional(), // Used to resolve exact sub-account in group accounts
  submissionId: z.string().uuid().optional(), // Client-generated idempotency key — prevents duplicate orders on network retry
  // True-move: when the customer chose "Move this order" to a different date, the id of the
  // order being moved. After the target-date write succeeds, the server soft-cancels this
  // order (same account only). Riding in the SAME request makes the move atomic from the
  // client's perspective — no fire-and-forget second call to get lost.
  moveFromOrderId: z.number().int().positive().optional(),
  // The account the ordering page is DISPLAYING. The server refuses (409 ACCOUNT_MISMATCH)
  // when it differs from the session-resolved account, instead of writing to the session's
  // account (2026-09-17 wrong-account overwrite). Optional only so pre-fix clients keep
  // working — TODO: make required once the new client has been live for a few weeks.
  accountId: z.number().int().positive().optional(),
});

export type Organization = typeof organizations.$inferSelect;
export type OrganizationCredentials = typeof organizationCredentials.$inferSelect;
export type OrderWindowConfig = typeof orderWindowConfigs.$inferSelect;
export type NotificationDefaults = typeof notificationDefaults.$inferSelect;
export type MarketReport = typeof marketReports.$inferSelect;
export type OrderAlertSubscription = typeof orderAlertSubscriptions.$inferSelect;
export type InsertOrderAlertSubscription = typeof orderAlertSubscriptions.$inferInsert;
export type Account = typeof accounts.$inferSelect;
export type DeliveryZone = typeof deliveryZones.$inferSelect;
export type MagicLink = typeof magicLinks.$inferSelect;
export type EmailVerification = typeof emailVerifications.$inferSelect;
export type CustomerSession = typeof customerSessions.$inferSelect;
export type KioskSession = typeof kioskSessions.$inferSelect;
export type AdminSession = typeof adminSessions.$inferSelect;
export type SuperAdminSession = typeof superAdminSessions.$inferSelect;
export type Category = typeof categories.$inferSelect;
export type Product = typeof products.$inferSelect;
export type CustomerType = typeof customerTypes.$inferSelect;
export type CustomerTypeMenuRule = typeof customerTypeMenuRules.$inferSelect;
export type CustomerTypePrice = typeof customerTypePrices.$inferSelect;
export type Order = typeof orders.$inferSelect;
export type OrderItem = typeof orderItems.$inferSelect;
export type User = typeof users.$inferSelect;
export type IntegrationSettings = typeof integrationSettings.$inferSelect;

export type InsertOrganization = z.infer<typeof insertOrganizationSchema>;
export type InsertOrganizationCredentials = z.infer<typeof insertOrganizationCredentialsSchema>;
export type InsertOrderWindowConfig = z.infer<typeof insertOrderWindowConfigSchema>;
export type InsertNotificationDefaults = z.infer<typeof insertNotificationDefaultsSchema>;
export type InsertMarketReport = z.infer<typeof insertMarketReportSchema>;
export type InsertAccount = z.infer<typeof insertAccountSchema>;
export type InsertMagicLink = z.infer<typeof insertMagicLinkSchema>;
export type InsertEmailVerification = z.infer<typeof insertEmailVerificationSchema>;
export type InsertCustomerSession = z.infer<typeof insertCustomerSessionSchema>;
export type InsertKioskSession = z.infer<typeof insertKioskSessionSchema>;
export type InsertAdminSession = z.infer<typeof insertAdminSessionSchema>;
export type InsertSuperAdminSession = z.infer<typeof insertSuperAdminSessionSchema>;
export type InsertCategory = z.infer<typeof insertCategorySchema>;
export type InsertProduct = z.infer<typeof insertProductSchema>;
export type InsertOrder = z.infer<typeof insertOrderSchema>;
export type InsertOrderItem = z.infer<typeof insertOrderItemSchema>;
export type InsertUser = z.infer<typeof insertUserSchema>;
export type InsertIntegrationSettings = z.infer<typeof insertIntegrationSettingsSchema>;
export type CustomerOrderInput = z.infer<typeof customerOrderSchema>;

// Extended types for API responses
export type OrderWithDetails = Order & {
  account: Account;
  items: (OrderItem & { product: Product })[];
};

export type DashboardStats = {
  totalOrders: number;
  totalItems: number;
  pendingOrders: number;
  reviewedOrders: number;
  sentToQuickbooksOrders: number;
  cancelledOrders: number;
  activeAccounts: number;
};

// QuickBooks integration types
export type QuickBooksInvoiceItem = {
  productId?: number; // FreshOrder product ID for back-linking after QB item creation
  productName: string;
  productSubheading?: string; // The product's subheading: on the line Description only for an org with qb_line_subheading; the Description of a QuickBooks item the send creates
  quantity: number;
  unitPrice?: number; // The FreshOrder price at send time for THIS customer — their customer type's price when they have one (takes priority over QB pricing)
  baseUnitPrice?: number; // The product's own price (products.price) at send time: the only price a NEWLY CREATED QuickBooks item is given — never a customer-type price
  taxPercent?: number; // Tax percent captured at order time
  description?: string;
  quickbooksItemId?: string | null; // Stored QB item ID for direct lookup
};

export type QuickBooksInvoiceData = {
  customerName: string;
  customerPhone: string;
  quickbooksCustomerId?: string | null; // Stored QB customer ID for direct lookup
  items: QuickBooksInvoiceItem[];
  notes?: string;
  orderDate: string;
  orderId: number;
  // Set when the order is already recorded as sent (an admin-confirmed resend):
  // the send this one replaces. Gives the resend a requestid of its own and keeps
  // its recovery off the earlier invoice (QB-3).
  previousSend?: { syncedAt: string; invoiceId: string | null } | null;
};

export type QuickBooksAuthState = {
  isAuthenticated: boolean;
  accessToken?: string;
  refreshToken?: string;
  companyId?: string;
  expiresAt?: number;
};

export const quickbooksInvoiceSchema = z.object({
  orderIds: z.array(z.number()),
  linkDecisions: z.array(z.object({
    accountId: z.number(),
    decision: z.enum(['link', 'create_new']),
    qbCustomerId: z.string().optional()
  })).optional(),
  productLinkDecisions: z.array(z.object({
    productId: z.number(),
    qbItemId: z.string(),
  })).optional(),
  confirmedResend: z.boolean().optional() // Override server-side duplicate-send guard (mirrors client confirmation dialog)
});

// Valid days of the week
const VALID_DAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"] as const;

// SMS Reminder settings schema
// All fields are nullable — null means "inherit org default" (Three-Tier Override Architecture)
export const reminderSettingsSchema = z.object({
  reminderEnabled: z.boolean().nullable(),
  customReminderMessage: z.string().nullable().optional(),
  firstReminderDay: z.enum(VALID_DAYS, { errorMap: () => ({ message: "Day must be one of: sunday, monday, tuesday, wednesday, thursday, friday, saturday" }) }).nullable(),
  firstReminderTime: z.string().regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, "Time must be in HH:MM format").nullable(),
  secondReminderDay: z.enum(VALID_DAYS, { errorMap: () => ({ message: "Day must be one of: sunday, monday, tuesday, wednesday, thursday, friday, saturday" }) }).nullable(),
  secondReminderTime: z.string().regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, "Time must be in HH:MM format").nullable(),
  smsOrderReceipts: z.boolean().nullable(),
});

// Email Notification settings schema
// All fields are nullable — null means "inherit org default"
export const emailNotificationSchema = z.object({
  emailNotificationEnabled: z.boolean().nullable(),
  emailAddress: z.string().email("Please enter a valid email address").nullable().optional(),
  customEmailMessage: z.string().nullable().optional(),
  firstEmailDay: z.enum(VALID_DAYS, { errorMap: () => ({ message: "Day must be one of: sunday, monday, tuesday, wednesday, thursday, friday, saturday" }) }).nullable(),
  firstEmailTime: z.string().regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, "Time must be in HH:MM format").nullable(),
  secondEmailDay: z.enum(VALID_DAYS, { errorMap: () => ({ message: "Day must be one of: sunday, monday, tuesday, wednesday, thursday, friday, saturday" }) }).nullable(),
  secondEmailTime: z.string().regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, "Time must be in HH:MM format").nullable(),
  emailOrderReceipts: z.boolean().nullable(),
});

export type QuickBooksInvoiceRequest = z.infer<typeof quickbooksInvoiceSchema>;
export type ReminderSettings = z.infer<typeof reminderSettingsSchema>;
export type EmailNotificationSettings = z.infer<typeof emailNotificationSchema>;

export type InsertAuditLog = z.infer<typeof insertAuditLogSchema>;
export type AuditLog = typeof auditLogs.$inferSelect;

// ==========================================
// QUICKBOOKS CACHE
// ==========================================

// QuickBooks lifetime value cache — stores per-account QB data locally
// to avoid slow external API calls on every account page load
export const quickbooksCacheEntries = pgTable("quickbooks_cache_entries", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").notNull().references(() => organizations.id),
  accountId: integer("account_id").notNull().references(() => accounts.id),
  totalRevenue: integer("total_revenue"),           // stored in cents to avoid float issues
  invoiceCount: integer("invoice_count"),
  averageInvoiceValue: integer("average_invoice_value"), // stored in cents
  lastSyncedAt: timestamp("last_synced_at"),
  syncStatus: varchar("sync_status", { length: 20 }).default("pending"), // 'pending' | 'success' | 'error'
  syncError: text("sync_error"),                    // last error message if sync failed
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => ({
  uniqueAccountOrg: uniqueIndex("qb_cache_account_org_idx").on(table.organizationId, table.accountId),
}));

export type QuickbooksCacheEntry = typeof quickbooksCacheEntries.$inferSelect;
export type InsertQuickbooksCacheEntry = typeof quickbooksCacheEntries.$inferInsert;

// ==========================================
// AUTHORIZED CONTACTS
// ==========================================

// Per-account authorized contacts — additional people who can log in and place orders
// for this account using their own phone number and PIN.
export const accountAuthorizedContacts = pgTable("account_authorized_contacts", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").notNull().references(() => organizations.id),
  accountId: integer("account_id").notNull().references(() => accounts.id),
  phone: varchar("phone", { length: 20 }).notNull(),
  name: text("name"), // Optional label — "Chef Mike", "Sarah (manager)"
  pin: varchar("pin", { length: 255 }), // Hashed PIN — each contact has their own
  pinSetAt: timestamp("pin_set_at"),
  requiresPinSetup: boolean("requires_pin_setup").notNull().default(true),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => [
  // Prevent duplicate phone per org (one phone can't be authorized on two accounts in same org)
  uniqueIndex("authorized_contacts_org_phone_unique").on(table.organizationId, table.phone),
]);

export const insertAccountAuthorizedContactSchema = createInsertSchema(accountAuthorizedContacts).pick({
  organizationId: true,
  accountId: true,
  phone: true,
  name: true,
});

export type AccountAuthorizedContact = typeof accountAuthorizedContacts.$inferSelect;
export type InsertAccountAuthorizedContact = z.infer<typeof insertAccountAuthorizedContactSchema>;

// ==========================================
// SMS / TEXT-TO-ORDER AGENT ("robot Kyle")
// ==========================================
// Phase 0 data model for the inbound SMS ordering agent. Every read of these
// tables MUST be org-scoped and bounded (50 default / 200 max) per the
// architectural rules. See BRIEF-text-to-order-agent.md §9.

// One conversation thread per buyer per channel (the buyer sees a single number).
export const agentConversations = pgTable("agent_conversations", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").notNull().references(() => organizations.id),
  customerPhone: varchar("customer_phone", { length: 20 }).notNull(), // E.164, e.g. +15195551234
  customerEmail: varchar("customer_email", { length: 255 }), // optional, for cross-channel later
  customerName: text("customer_name"), // resolved account name when known
  primaryChannel: varchar("primary_channel", { length: 20 }).notNull().default("sms"), // 'sms' (WhatsApp etc. deferred)
  lastMessageAt: timestamp("last_message_at"), // for bounded, ordered console thread lists
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (table) => [
  uniqueIndex("agent_conversations_org_phone_channel_unique").on(table.organizationId, table.customerPhone, table.primaryChannel),
  index("agent_conversations_org_last_message_idx").on(table.organizationId, table.lastMessageAt),
]);

// Per-message log with attribution. providerMessageId is the Twilio MessageSid —
// a dedicated, indexed column so inbound dedup (webhook re-delivery / double "yes")
// is DB-enforced. This is conversation-level idempotency, DISTINCT from order
// submissionId. NULLs are allowed (system/agent messages have no SID) and Postgres
// treats them as distinct, so the unique index never blocks outbound rows.
export const agentMessages = pgTable("agent_messages", {
  id: serial("id").primaryKey(),
  conversationId: integer("conversation_id").notNull().references(() => agentConversations.id),
  organizationId: integer("organization_id").notNull().references(() => organizations.id),
  channel: varchar("channel", { length: 20 }).notNull().default("sms"),
  role: varchar("role", { length: 50 }).notNull(), // 'user' | 'agent' | 'system' | 'human:<userId>'
  content: text("content").notNull(),
  metadata: json("metadata").$type<Record<string, unknown>>(), // full Twilio payload, etc.
  providerMessageId: varchar("provider_message_id", { length: 64 }), // Twilio MessageSid (SMxxxx) — inbound dedup key
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => [
  uniqueIndex("agent_messages_org_provider_msg_unique").on(table.organizationId, table.providerMessageId),
  index("agent_messages_conversation_idx").on(table.conversationId),
]);

// Voice exemplars (Phase 3A). Entries may be labeled objects (source-tagged, so
// auto-harvest eviction can spare manually-seeded ones) or plain strings (legacy /
// hand-entered — treated as 'seed'). Stored in the existing json column; the shape
// is a code-level convention, not a schema migration.
export type VoiceExemplarSource = "seed" | "takeover" | "relay";
export interface VoiceExemplarEntry {
  text: string;
  source: VoiceExemplarSource;
  at?: string; // ISO timestamp when added/harvested
}
export type VoiceExemplar = string | VoiceExemplarEntry;

// Per-org agent configuration. One row per org. agentEnabled defaults false and
// oversightMode defaults 'review_all' so a freshly-enabled org is human-approval-first
// and graduates to 'auto_confirm' deliberately. modelId is mid-tier by default — never
// hardcode Opus.
export const agentSettings = pgTable("agent_settings", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").notNull().references(() => organizations.id),
  agentEnabled: boolean("agent_enabled").notNull().default(false),
  agentName: varchar("agent_name", { length: 100 }), // persona name, e.g. "Kyle"
  styleCard: text("style_card"), // distilled voice/style description for the prompt
  voiceExemplars: json("voice_exemplars").$type<VoiceExemplar[]>(), // 5–15 of the admin's real outbound texts (labeled — see VoiceExemplarEntry)
  modelId: varchar("model_id", { length: 100 }).notNull().default("claude-sonnet-5-5"), // mid-tier default (current Sonnet); per-org configurable. Prod rows keep their stored value — set CNP explicitly at go-live
  contextWindowMessages: integer("context_window_messages").notNull().default(20), // caps LLM history (heap/OOM guard)
  oversightMode: varchar("oversight_mode", { length: 20 }).notNull().default("review_all"), // 'review_all' | 'auto_confirm'
  escalationTriggers: json("escalation_triggers").$type<Record<string, unknown>>(), // which events escalate to the admin
  supervisorPhone: varchar("supervisor_phone", { length: 20 }), // E.164; who the agent texts when it escalates (nullable)
  // The supervisor's name as buyers know it ("Kyle"). The console's compose sends
  // as this person on a thread with no rep of its own (a 1:1), prefixed "Kyle: ".
  // Never free text at send time — set here or per thread (agent_threads.rep_name).
  supervisorName: varchar("supervisor_name", { length: 100 }),
  // Trust ladder threshold: after this many clean buyer YESes in a row an account's
  // clean order texts are placed without a recap. NULL = ladder off (every org at
  // launch). Only 2, 3 or 5 are accepted (PATCH /agent/settings).
  clydeAutoPlaceAfter: integer("clyde_auto_place_after"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (table) => [
  uniqueIndex("agent_settings_org_unique").on(table.organizationId),
]);

// SMS consent ledger (TCPA/CASL). STOP/HELP and the one-time opt-in link write here.
// The first agent order must pass an 'opted_in' status.
export const smsConsent = pgTable("sms_consent", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").notNull().references(() => organizations.id),
  customerPhone: varchar("customer_phone", { length: 20 }).notNull(), // E.164
  consentStatus: varchar("consent_status", { length: 20 }).notNull().default("unknown"), // 'unknown' | 'opted_in' | 'opted_out'
  source: varchar("source", { length: 50 }), // 'opt_in_link' | 'sms_start' | 'sms_stop' | 'admin'
  optedInAt: timestamp("opted_in_at"),
  optedOutAt: timestamp("opted_out_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (table) => [
  uniqueIndex("sms_consent_org_phone_unique").on(table.organizationId, table.customerPhone),
]);

// The held parse carried by a 'product_match' escalation: what an order text
// resolved to and what it didn't. Terms are the buyer's VERBATIM words and qty is
// the RAW parsed number (may be fractional — "1/2 cabbage" → 0.5 — or null when
// the line had no number, "Skid spuds"). Phase C's per-account learning depends
// on that verbatim/raw signal — never normalize or round it here. A type alias
// (not an interface) so it satisfies the json column's Record<string, unknown>
// without a cast. `matchedName` marks a line that DID match but tripped the
// quantity ceiling — the term is recognized; the quantity is the question.
export type ProductMatchPayload = {
  resolvedLines: Array<{ name: string; qty: number }>; // canonical catalog names
  unresolvedLines: Array<{ term: string; qty: number | null; matchedName?: string }>;
  requestedDate: string | null; // YYYY-MM-DD as parsed, unvalidated
  // Group thread only (docs/clyde/group-thread-transport.md §4 rule 7): Clyde asked
  // the buyer about the terms in the thread instead of holding in silence. The row
  // is still the console's fallback; nudgedAt records the ONE supervisor nudge the
  // sweep sends when nobody answers (server/services/agentTermNudge.ts).
  threadQuestion?: { askedAt: string; nudgedAt: string | null };
};

// Escalation queue — the agent's "I need a human" moments (non-order question,
// reviewed/synced-order edit, unknown account, parse/create failure, order terms
// the catalog+aliases can't resolve). Rows are surfaced in the oversight console
// and answered via console take-over or an admin SMS reply. Bounded + org-scoped
// reads per the architectural rules.
export const agentEscalations = pgTable("agent_escalations", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").notNull().references(() => organizations.id),
  conversationId: integer("conversation_id").notNull().references(() => agentConversations.id),
  messageId: integer("message_id").references(() => agentMessages.id), // the buyer turn that triggered it
  type: varchar("type", { length: 30 }).notNull(), // 'question' | 'reviewed_edit' | 'synced_edit' | 'unknown_account' | 'parse_error' | 'order_create_failed' | 'product_match'
  status: varchar("status", { length: 20 }).notNull().default("open"), // 'open' | 'resolved' | 'dismissed'
  detail: text("detail"), // the buyer's question / context — what goes into the admin SMS + digest
  payload: json("payload").$type<Record<string, unknown>>(), // structured context per type — ProductMatchPayload for 'product_match'
  orderId: integer("order_id").references(() => orders.id), // the locked order, for edit escalations
  resolvedBy: varchar("resolved_by", { length: 50 }), // 'human:<userId>' (console) or the supervisor phone (SMS reply)
  resolvedAt: timestamp("resolved_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (table) => [
  index("agent_escalations_org_status_idx").on(table.organizationId, table.status, table.createdAt),
  index("agent_escalations_conversation_idx").on(table.conversationId),
  // At most one OPEN escalation per (org, conversation, type) — DB-enforces the
  // recordEscalation dedup so a rapid double-text can't stack duplicate flags.
  uniqueIndex("agent_escalations_open_unique")
    .on(table.organizationId, table.conversationId, table.type)
    .where(sql`${table.status} = 'open'`),
]);

// Per-account vocabulary (Phase C of BRIEF-agent-unmatched-to-admin.md): what a
// TERM means for ONE buyer — org-wide aliases can't carry account-specific
// meaning ("carrot" = 50lb Jumbo only for The Snug; "green zucchini" = the 25lb
// box for one buyer, the BU BOX for another). Consulted BEFORE org-wide aliases
// in the agent's deterministic resolve (exact catalog names always win over
// both). `defaultQty` is NOT optional polish — Pet Wholesale's "1/2 cabbage" =
// 15 × Green Cabbage 792: the term encodes SKU + unit + arithmetic none of
// which is inferrable from text or catalog. It is set only when the buyer's
// line carried no clean integer (the term itself encodes the quantity) and is
// applied only when a line has NO number — an explicit buyer number always
// wins. The recap-confirm is the safety net for every inference: the buyer
// sees canonical names + final quantities and assents before any order exists.
// Learned from Phase B console resolutions (scope "this buyer"); rows are
// admin-taught, never LLM-written.
export const accountProductTerms = pgTable("account_product_terms", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").notNull().references(() => organizations.id),
  accountId: integer("account_id").notNull().references(() => accounts.id),
  productId: integer("product_id").notNull().references(() => products.id),
  term: text("term").notNull(), // normalized alias-text form (normalizeAliasText)
  defaultQty: integer("default_qty"), // qty the term itself encodes ("skid spuds" → 50); null = names the product only
  unit: varchar("unit", { length: 30 }), // optional human label for what defaultQty counts ("cases") — display only
  source: varchar("source", { length: 20 }).notNull().default("resolution"), // 'resolution' (Phase B card) | 'manual' (future editor)
  seenCount: integer("seen_count").notNull().default(1),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (table) => [
  // One meaning per term per buyer — re-teaching the same term re-points it (latest wins).
  uniqueIndex("account_product_terms_org_account_term_unique").on(table.organizationId, table.accountId, table.term),
  index("account_product_terms_org_account_idx").on(table.organizationId, table.accountId),
]);

// Clyde threads (docs/clyde/conversations-webhook-contract.md §6). One row per
// Twilio Conversation the agent takes part in — a group thread (buyer + rep +
// Clyde) or a 1:1. The row is the ONLY thing an inbound Conversations webhook
// routes on: conversation_sid → org → account. The author's number alone never
// resolves a tenant. agent_conversation_id is the message thread the agent core
// reads (getRecentAgentMessages); one buyer address ↔ one Clyde number is one
// conversation at Twilio (50416), so the two rows map 1:1. channel is a property
// of the thread ('sms' | 'whatsapp'), never a fork of the transport.
export const agentThreads = pgTable("agent_threads", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").notNull().references(() => organizations.id),
  accountId: integer("account_id").references(() => accounts.id), // null only on a 1:1 bootstrap whose phone matched no account
  agentConversationId: integer("agent_conversation_id").notNull().references(() => agentConversations.id),
  conversationSid: varchar("conversation_sid", { length: 34 }).notNull(), // Twilio CH…
  channel: varchar("channel", { length: 20 }).notNull().default("sms"), // 'sms' | 'whatsapp'
  projectedAddress: varchar("projected_address", { length: 32 }).notNull(), // Clyde's number for this org (whatsapp:-prefixed on WhatsApp)
  buyerAddress: varchar("buyer_address", { length: 32 }).notNull(), // +1… or whatsapp:+1…
  repAddress: varchar("rep_address", { length: 32 }), // null = 1:1 thread
  repName: varchar("rep_name", { length: 100 }), // the rep's first name as the buyer knows it — the console sends "Kyle: …" as them
  createdBy: varchar("created_by", { length: 50 }).notNull(), // 'admin:<userId>' | 'inbound'
  status: varchar("status", { length: 20 }).notNull().default("active"), // 'active' | 'closed'
  lastInboundAt: timestamp("last_inbound_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (table) => [
  uniqueIndex("agent_threads_conversation_sid_unique").on(table.conversationSid),
  uniqueIndex("agent_threads_org_conversation_unique").on(table.organizationId, table.agentConversationId),
  index("agent_threads_org_account_idx").on(table.organizationId, table.accountId),
]);

export const agentConversationsRelations = relations(agentConversations, ({ one, many }) => ({
  organization: one(organizations, {
    fields: [agentConversations.organizationId],
    references: [organizations.id],
  }),
  messages: many(agentMessages),
}));

export const agentMessagesRelations = relations(agentMessages, ({ one }) => ({
  conversation: one(agentConversations, {
    fields: [agentMessages.conversationId],
    references: [agentConversations.id],
  }),
  organization: one(organizations, {
    fields: [agentMessages.organizationId],
    references: [organizations.id],
  }),
}));

export const agentSettingsRelations = relations(agentSettings, ({ one }) => ({
  organization: one(organizations, {
    fields: [agentSettings.organizationId],
    references: [organizations.id],
  }),
}));

export const smsConsentRelations = relations(smsConsent, ({ one }) => ({
  organization: one(organizations, {
    fields: [smsConsent.organizationId],
    references: [organizations.id],
  }),
}));

export const agentEscalationsRelations = relations(agentEscalations, ({ one }) => ({
  organization: one(organizations, {
    fields: [agentEscalations.organizationId],
    references: [organizations.id],
  }),
  conversation: one(agentConversations, {
    fields: [agentEscalations.conversationId],
    references: [agentConversations.id],
  }),
}));

export const insertAgentConversationSchema = createInsertSchema(agentConversations).pick({
  organizationId: true,
  customerPhone: true,
  customerEmail: true,
  customerName: true,
  primaryChannel: true,
});

export const insertAgentMessageSchema = createInsertSchema(agentMessages).pick({
  conversationId: true,
  organizationId: true,
  channel: true,
  role: true,
  content: true,
  metadata: true,
  providerMessageId: true,
});

export const insertAgentSettingsSchema = createInsertSchema(agentSettings).pick({
  organizationId: true,
  agentEnabled: true,
  agentName: true,
  styleCard: true,
  voiceExemplars: true,
  modelId: true,
  contextWindowMessages: true,
  oversightMode: true,
  escalationTriggers: true,
  supervisorPhone: true,
  supervisorName: true,
  clydeAutoPlaceAfter: true,
});

export const insertAgentEscalationSchema = createInsertSchema(agentEscalations).pick({
  organizationId: true,
  conversationId: true,
  messageId: true,
  type: true,
  status: true,
  detail: true,
  payload: true,
  orderId: true,
});

export const insertSmsConsentSchema = createInsertSchema(smsConsent).pick({
  organizationId: true,
  customerPhone: true,
  consentStatus: true,
  source: true,
  optedInAt: true,
  optedOutAt: true,
});

export type AgentConversation = typeof agentConversations.$inferSelect;
export type InsertAgentConversation = z.infer<typeof insertAgentConversationSchema>;
export type AgentMessage = typeof agentMessages.$inferSelect;
export type InsertAgentMessage = z.infer<typeof insertAgentMessageSchema>;
export type AgentSettings = typeof agentSettings.$inferSelect;
export type InsertAgentSettings = z.infer<typeof insertAgentSettingsSchema>;
export type SmsConsent = typeof smsConsent.$inferSelect;
export type InsertSmsConsent = z.infer<typeof insertSmsConsentSchema>;
export type AgentEscalation = typeof agentEscalations.$inferSelect;
export type AccountProductTerm = typeof accountProductTerms.$inferSelect;
export const insertAgentThreadSchema = createInsertSchema(agentThreads).pick({
  organizationId: true,
  accountId: true,
  agentConversationId: true,
  conversationSid: true,
  channel: true,
  projectedAddress: true,
  buyerAddress: true,
  repAddress: true,
  repName: true,
  createdBy: true,
  status: true,
});
export type AgentThread = typeof agentThreads.$inferSelect;
export type InsertAgentThread = z.infer<typeof insertAgentThreadSchema>;
export type AgentThreadChannel = "sms" | "whatsapp";
export type InsertAgentEscalation = z.infer<typeof insertAgentEscalationSchema>;

// ==========================================
// Demo lead capture (GTM funnel — Phase 0 task 6)
// ==========================================
// A prospect who built an order in the public /demo and then asked for a
// walkthrough. This row IS the CASL express-consent record: the email they
// typed + the exact timestamp + the demo order they built + the marketing
// attribution that brought them.
//
// Deliberately NOT tenant-scoped the way buyer data is. The demo runs under the
// `demo-public` org, but a lead is a marketing contact, not a customer — it must
// never surface in any tenant-scoped list query, and it must OUTLIVE the
// ephemeral demo account/order (both swept on a 24h cron). So `demoAccountId`
// and `demoOrderId` are plain ints with NO foreign key (the referenced rows are
// gone within a day), and `orderSummary` is a durable text snapshot of what they
// built, captured server-side at lead time so the record stays self-contained.
export const demoLeads = pgTable("demo_leads", {
  id: serial("id").primaryKey(),
  email: varchar("email", { length: 255 }).notNull(),
  name: varchar("name", { length: 120 }),
  phone: varchar("phone", { length: 30 }),
  company: varchar("company", { length: 160 }),
  message: text("message"),
  // Context (no FK — ephemeral demo rows are swept within 24h; see note above).
  demoAccountId: integer("demo_account_id"),
  demoOrderId: integer("demo_order_id"),
  orderSummary: text("order_summary"), // durable snapshot, e.g. "3x Romaine, 2x Strawberries"
  source: varchar("source", { length: 40 }).notNull().default("demo_admin_view"),
  // First-touch marketing attribution (captured client-side, see lib/analytics.ts).
  utmSource: varchar("utm_source", { length: 120 }),
  utmMedium: varchar("utm_medium", { length: 120 }),
  utmCampaign: varchar("utm_campaign", { length: 160 }),
  utmTerm: varchar("utm_term", { length: 160 }),
  utmContent: varchar("utm_content", { length: 160 }),
  referrer: text("referrer"),        // document.referrer at first touch
  landingPath: text("landing_path"), // first path this visitor hit
  ipAddress: varchar("ip_address", { length: 64 }), // abuse triage only
  userAgent: text("user_agent"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => [
  index("demo_leads_created_idx").on(table.createdAt),
  index("demo_leads_email_idx").on(table.email),
]);

// Server derives ipAddress / userAgent / orderSummary — the client never sets them.
export const insertDemoLeadSchema = createInsertSchema(demoLeads).pick({
  email: true,
  name: true,
  phone: true,
  company: true,
  message: true,
  demoAccountId: true,
  demoOrderId: true,
  orderSummary: true,
  source: true,
  utmSource: true,
  utmMedium: true,
  utmCampaign: true,
  utmTerm: true,
  utmContent: true,
  referrer: true,
  landingPath: true,
  ipAddress: true,
  userAgent: true,
});

export type DemoLead = typeof demoLeads.$inferSelect;
export type InsertDemoLead = z.infer<typeof insertDemoLeadSchema>;

// ==========================================
// NATIVE APP: Push-notification device tokens (Capacitor shell, Phase 2)
// ==========================================
// One row per BINDING: a (device token, organization, account) triple. The
// native shell is multi-vendor, so one phone signed in to two distributors
// holds two rows with the same token — each org's reminders reach it. The
// unique key used to be the token alone, so signing in to a second vendor
// re-pointed the only row and silently cut the first vendor's reminders off
// (TestFlight build 6, 2026-09-17). Token liveness is a property of the
// device, not the binding: a token APNs/FCM reports dead (410 / UNREGISTERED)
// is disabled on EVERY row that carries it, and a re-registration of that
// token revives them all. Rows are removed only by an explicit sign-out
// (/customer/push/unregister), which is scoped to the caller's own binding.
// Prod db:push is MANUAL (see CLAUDE.md Deployment).

export const devicePushTokens = pgTable("device_push_tokens", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").notNull().references(() => organizations.id),
  accountId: integer("account_id").notNull().references(() => accounts.id),
  token: text("token").notNull(),
  platform: varchar("platform", { length: 10 }).notNull(), // 'ios' | 'android'
  deviceId: varchar("device_id", { length: 64 }), // app-generated install id (best effort)
  disabledAt: timestamp("disabled_at"), // set when the push provider reports the token dead
  createdAt: timestamp("created_at").notNull().defaultNow(),
  lastSeenAt: timestamp("last_seen_at").notNull().defaultNow(),
}, (table) => [
  // Leads with token, so the provider-driven disable/revive-by-token also uses it.
  uniqueIndex("device_push_tokens_token_org_account_idx").on(table.token, table.organizationId, table.accountId),
  index("device_push_tokens_org_account_idx").on(table.organizationId, table.accountId),
]);

export type DevicePushToken = typeof devicePushTokens.$inferSelect;

// ==========================================
// TENANT ONBOARDING INTAKE
// ==========================================
// Pre-tenant intake: a prospect fills this in BEFORE their organization exists,
// so these tables deliberately have NO organizationId and are NOT reachable
// through the org-scoped storage layer. Do not add them to storage.ts — every
// invariant there is about organizationId scoping, and a reader who found them
// alongside org-scoped tables would reasonably assume scoping applies.
// Access lives in server/onboarding.ts.
//
// The link is the credential: `token` is 32 bytes of crypto randomness and is
// the ONLY thing gating read/write on a submission, so it must never be
// guessable, logged, or reused across prospects.
//
// Answers are JSONB rather than columns on purpose — the question set is still
// changing shape weekly, and a migration per wording change is not worth it at
// this volume. When the shape settles, promote the stable answers to columns.
// Additive tables; prod db:push is MANUAL (see CLAUDE.md Deployment).

export const onboardingSubmissions = pgTable("onboarding_submissions", {
  id: serial("id").primaryKey(),
  token: varchar("token", { length: 64 }).notNull().unique(),
  // Jon's own label for the prospect, set when the link is created.
  prospectName: varchar("prospect_name", { length: 200 }).notNull(),
  prospectEmail: varchar("prospect_email", { length: 255 }),
  answers: json("answers").notNull().default({}),
  currentStep: integer("current_step").notNull().default(0),
  // 'draft' while they are filling it in (autosaved), 'submitted' once sent.
  status: varchar("status", { length: 20 }).notNull().default("draft"),
  submittedAt: timestamp("submitted_at"),
  lastSeenAt: timestamp("last_seen_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (table) => [
  index("onboarding_submissions_status_idx").on(table.status),
]);

// Uploads (product list, client list, logo). Stored as base64 in the DB to match
// the existing product-image and branding pattern — no object store in play.
export const onboardingFiles = pgTable("onboarding_files", {
  id: serial("id").primaryKey(),
  submissionId: integer("submission_id").notNull().references(() => onboardingSubmissions.id, { onDelete: "cascade" }),
  kind: varchar("kind", { length: 40 }).notNull(), // 'product_list' | 'client_list' | 'logo'
  filename: varchar("filename", { length: 255 }).notNull(),
  mimeType: varchar("mime_type", { length: 128 }).notNull(),
  sizeBytes: integer("size_bytes").notNull(),
  data: text("data").notNull(), // base64
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => [
  index("onboarding_files_submission_idx").on(table.submissionId),
]);

export type OnboardingSubmission = typeof onboardingSubmissions.$inferSelect;
export type OnboardingFile = typeof onboardingFiles.$inferSelect;

// Client-writable surface only. token/status/submittedAt are server-controlled.
export const onboardingAnswersSchema = z.object({
  answers: z.record(z.any()),
  currentStep: z.number().int().min(0).max(50),
});

// ==========================================
// Stock image library (platform-wide, NOT tenant-scoped)
// ==========================================

// A curated library of generic produce photography, shared by every tenant and
// used to illustrate a freshly-imported catalogue without hand-uploading each
// product. Deliberately has NO organizationId — these rows belong to the
// platform, not to any org, and are only ever COPIED into products.image_data.
//
// Base64 in the DB, matching the existing product-image / branding / onboarding
// pattern — there is no object store in play.
//
// `section` separates reusable stock from imagery that is not ours to reuse:
//   'produce'         — generic, safe to match onto any tenant's catalogue
//   'brand-packshots' — third-party manufacturer photography (Taylor Farms,
//                       Covered Bridge). Never auto-matched; a human must pick
//                       it deliberately for a tenant that stocks that brand.
//   'lifestyle'       — farm/warehouse marketing imagery, not product shots.
export const stockImages = pgTable("stock_images", {
  id: serial("id").primaryKey(),
  section: varchar("section", { length: 32 }).notNull(),
  category: varchar("category", { length: 64 }).notNull(),
  slug: varchar("slug", { length: 160 }).notNull(),
  name: text("name").notNull(),           // human label, e.g. "gala apples pouch"
  // Set false where the name was inferred rather than confirmed — the source
  // generator truncated its filenames and cut off distinguishing detail
  // (grade numbers, pack sizes). Ranked lower and surfaced in review.
  verified: boolean("verified").notNull().default(true),
  imageData: text("image_data").notNull(),      // base64 webp, full size
  imageMimeType: text("image_mime_type").notNull().default("image/webp"),
  thumbData: text("thumb_data").notNull(),      // base64 webp, ~200px, for review UI
  sourcePath: text("source_path"),              // provenance in the origin library
  isActive: boolean("is_active").notNull().default(true),
  // Provenance of the bytes. 'seed' = loaded from the Drive library by
  // scripts/seed-stock-images.mjs; 'generated' = made in-app by the AI path;
  // 'uploaded' = a file the super-admin picked in the Match Images dialog.
  // A DRAFT is origin<>'seed' AND is_active=false: it exists only for the
  // reviewer who made it, is invisible to Browse and /match (both filter
  // is_active), and becomes a real library row only when applied to a product.
  origin: varchar("origin", { length: 16 }).notNull().default("seed"),
  generationModel: text("generation_model"),    // e.g. gpt-image-2.5-sunburst
  generationPrompt: text("generation_prompt"),  // the full prompt actually sent
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => [
  uniqueIndex("stock_images_slug_unique").on(table.section, table.category, table.slug),
  index("stock_images_section_idx").on(table.section),
]);

export type StockImage = typeof stockImages.$inferSelect;
// Metadata-only projection. NEVER select imageData/thumbData in a list context —
// 287 rows of base64 is tens of MB (see the payload rules in CLAUDE.md); the
// blobs are served per-id by their own endpoints.
export type StockImageMeta = Omit<StockImage, "imageData" | "thumbData">;
